import React from "react";
import { Folder } from "lucide-react";
import { CLASSES, FILE_ICON_SIZE } from "../../constants";

/**
 * Component to render a directory item in the file explorer
 * @param {Object} props - Component props
 * @param {Object} props.directory - Directory object with name, path, type, etc.
 * @param {number} props.level - Indentation level
 * @param {boolean} props.isExpanded - Whether this directory is expanded
 * @param {boolean} props.isSelected - Whether this directory is selected
 * @param {boolean} props.isLoading - Whether this directory is being loaded
 * @param {Function} props.onClick - Click handler
 * @param {React.ReactNode} props.children - Child elements (subdirectories and files)
 * @returns {JSX.Element} The directory item
 */
const DirectoryItem = ({
  directory,
  level = 0,
  isExpanded = false,
  isSelected = false,
  isLoading = false,
  onClick,
  children,
}) => {
  const paddingLeft = level * 16 + 8 + "px";

  const handleClick = () => {
    if (onClick) {
      onClick(directory.path);
    }
  };

  return (
    <div>
      <div
        className={`
          ${CLASSES.fileItem}
          ${isSelected ? CLASSES.fileItemSelected : ""}
        `}
        style={{ paddingLeft }}
        onClick={handleClick}
      >
        {/* Expansion arrow */}
        <span className="mr-1.5 text-neutral-500 dark:text-neutral-400 inline-block w-4 text-center transition-transform duration-200">
          {isExpanded ? "▼" : "▶"}
        </span>

        {/* Folder icon */}
        <Folder
          size={FILE_ICON_SIZE}
          className="mr-2 text-blue-500 dark:text-neutral-400"
        />

        {/* Directory name */}
        <span className="text-xs text-neutral-700 dark:text-neutral-300 font-medium">
          {directory.name}
        </span>

        {/* Loading spinner */}
        {isLoading && (
          <svg
            className="animate-spin ml-auto h-4 w-4 text-neutral-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
      </div>

      {/* Children (shown when expanded) */}
      {isExpanded && children && <div>{children}</div>}
    </div>
  );
};

export default DirectoryItem;
